/**
 * The address of a conversation: `/my/messages?c=42`,
 * `/cabinet/practice/messages?c=42`.
 *
 * A thread used to live in component state only, and every door into the
 * inbox — «Написать» in «Мои клиенты», «Переписка» in «Мои психологи»,
 * «Открыть переписку» after a first message — opened whichever thread had the
 * freshest message. A psychologist pressed «Написать» next to one client and
 * typed something confidential into ANOTHER client's thread. The id in the
 * address is what makes the door lead to the person it stands next to.
 *
 * Kept out of the zone's chunk — the router imports it eagerly to type the
 * routes, so anything heavy here would land in the entry bundle.
 */
export interface MessagesSearch {
  /** The conversation to open. */
  c?: number
}

/**
 * The router JSON-parses search values, so `?c=42` arrives as the NUMBER 42 —
 * and a hand-typed `?c="42"` as a string. Both mean the same thread; anything
 * else means no thread rather than an error, because this input comes from a
 * link somebody else wrote.
 */
export function parseMessagesSearch(search: Record<string, unknown>): MessagesSearch {
  const raw = search.c
  const id = typeof raw === 'number' ? raw : typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw) : NaN
  return Number.isSafeInteger(id) && id > 0 ? { c: id } : {}
}

/**
 * The tab of a session list: `/cabinet/practice/sessions?tab=history`,
 * `/my/sessions?tab=history`. «Предстоящие» is the default and has no
 * parameter.
 *
 * The door «Отметьте, как прошли встречи» on the practice dashboard needs it:
 * the sessions to mark are past ones, which live in «История», and the door
 * used to open «Предстоящие», where none of them is.
 */
export interface SessionsSearch {
  tab?: 'history'
}

export function parseSessionsSearch(search: Record<string, unknown>): SessionsSearch {
  return search.tab === 'history' ? { tab: 'history' } : {}
}
